/**
 * CORE-BL — Guided Onboarding & Product Education · role instruction
 *
 * The tracks the constitution asks for, written against what is true on
 * **current main** and only what the role can actually do.
 *
 * Applicability, not copy-paste
 * -----------------------------
 * Steps carry an audience. `DISPATCHER` and `SERVICE_WRITER` share the
 * front-of-house track because they share the board and the schedule — but a
 * dispatcher cannot create or edit tickets, so intake steps declare
 * `requiredCapability: 'canCreateTickets'` and simply do not appear for them.
 * One track, filtered, rather than two libraries that drift.
 *
 * **This is presentation, not authorization.** The capability keys below are
 * read from the same permission surface the UI already uses; they decide what
 * is TAUGHT, never what is ALLOWED. SP-03 and each capability remain the
 * authority, server-side. Nothing here grants anything.
 *
 * Written against current main
 * ----------------------------
 * This used to say ticket lifecycle, custody and timers were "being actively
 * rewritten on another lane" and that nothing here taught behaviour from that
 * unmerged work. That is history: the operational-truth lane MERGED into main
 * while CORE-BL was in flight, and the timer contract changed underneath this
 * file.
 *
 * The change that mattered: **Stop no longer completes anything.** The
 * technician step said *"stopping is what completes the ticket and hands it to
 * the front of house"*, which is false on current main. Stopping ends the
 * technician's measured work episode and sends the time to allocation review;
 * the ticket and its jobs stay open, and completion is a separate deliberate
 * act. A technician taught the old sentence would have believed a stopped
 * ticket was finished and handed over, and stopped looking at it.
 *
 * The steps below were re-read against the landed authority —
 * `TicketTimerControls`, `TimerAllocationReviewDialog`, `TicketCard` and
 * `usePermissions` — and `coreBlTechnicianTimerContract.test.ts` fails if that
 * vocabulary moves again.
 *
 * Where a mechanism is NOT clearly established on current main, the step stays
 * general rather than asserting one. Product education must be less specific
 * rather than false.
 *
 * Scope
 * -----
 * CORE-BL teaches **how to use OptiBay**. It does not teach how to repair a
 * vehicle — that is CORE-O, CORE-AH and CORE-AI, and the line is absolute.
 * Nothing below explains a repair, recommends training, or evaluates skill.
 */

import type { OnboardingStep } from '@/contexts/OnboardingSteps';
import type { AppRole, CapabilityKey, KnownAppRole } from './onboardingExperience';
import { canRoleOpen } from './routeReachability';

/**
 * A step plus who it is for.
 *
 * `requiredCapability` names a boolean on the app's existing permission
 * surface. When present, the step is shown only to people who actually hold
 * it — so nobody is taught a control that will not be there.
 */
export interface RoleAwareStep extends OnboardingStep {
  /** Restrict to specific roles. Omitted means every role on the track. */
  roles?: ReadonlyArray<AppRole>;
  /**
   * A route the step's CLAIM depends on, separate from `page`.
   *
   * The "find this again" steps promise permanent reference. That promise is
   * only true for roles the router lets into `/user-guide`.
   */
  requiresRoute?: string;
  /**
   * Permission key that must be true. Presentation gating only.
   *
   * Typed against the real permission surface: `'canCreateTikets'` used to
   * compile and silently delete the lesson, which is a drift path that looks
   * exactly like content nobody wrote.
   */
  requiredCapability?: CapabilityKey;
}

/**
 * The capabilities a viewer holds, as the UI already computes them.
 *
 * Passed in rather than read here, so this module stays pure and the
 * permission surface keeps its single owner.
 */
export type CapabilitySnapshot = Partial<Readonly<Record<CapabilityKey, unknown>>>;

// =============================================================================
// Technician — the four actions where misuse corrupts operational truth
// =============================================================================

/**
 * Deliberately small and operational.
 *
 * The constitution is specific about why this track matters: *"a technician
 * who cannot start a timer confidently writes times on paper"*, and CORE-AC,
 * CORE-AJ and CORE-AK are named as the places where incorrect use silently
 * corrupts truth rather than producing a visible error. Four or five things,
 * in context, in a couple of minutes.
 */
export const TECHNICIAN_EXPERIENCE_STEPS: RoleAwareStep[] = [
  {
    id: 'tech-your-work',
    title: 'Your work for today',
    description:
      'The Dayboard shows the tickets assigned to you, in the order they are scheduled. This is where your day starts — you do not need to go looking anywhere else.',
    page: '/?tab=dayboard',
    gateType: 'soft',
    coachmarks: ['dayboard-tab'],
    icon: 'ClipboardList',
  },
  {
    id: 'tech-open-ticket',
    title: 'Opening a ticket',
    description:
      'Select a ticket to see the vehicle, the jobs on it, and the bay it is in. Everything you need for that piece of work is on the card.',
    page: '/?tab=dayboard',
    gateType: 'soft',
    coachmarks: ['ticket-jobs-list'],
    icon: 'FileText',
  },
  {
    // Start is technician-scoped, and the scope is a HUMAN ASSERTION about
    // what the measured time belongs to. `beginStartScopeSelection` routes:
    // no open jobs starts nothing, one open job IS the selection, and two or
    // more opens a picker with NOTHING preselected — because a preselected
    // list somebody merely fails to change is passive acceptance, not
    // evidence of who worked on what. The copy must not imply every job is
    // picked up automatically.
    id: 'tech-start-work',
    title: 'Starting work',
    description:
      'Start your timer when you begin. If the ticket has more than one open job, you choose the job or jobs you are actually working on — nothing is selected for you, because the time you run is recorded against what you pick. If there is only one open job, that is your work and the timer just starts.',
    page: '/?tab=dayboard',
    gateType: 'soft',
    coachmarks: ['ticket-timer-controls'],
    icon: 'Timer',
  },
  {
    id: 'tech-pause',
    title: 'Stepping away — pause, then resume',
    description:
      'Pause when you break, go to lunch, or wait on a part. You give a short reason, paused time is not counted against the job, and you do not lose what you have already logged. Resume the same ticket when you come back. Pause is the action for stepping away — do not use Stop for it.',
    page: '/?tab=dayboard',
    gateType: 'soft',
    coachmarks: ['ticket-timer-controls'],
    icon: 'Pause',
  },
  {
    // REWRITTEN for the final-Stop completion law (20270102100000): the LAST
    // technician's Stop is the declaration that the BOH work is done and
    // completes the ticket; with a colleague still working it ends only this
    // technician's part. Operational completion is never pickup or payment.
    id: 'tech-stop-work',
    title: 'Ending a piece of work',
    description:
      'Stop Work ends the work you have been measuring and sends that time to be reviewed. If you are paused there is no running timer to stop, so the button reads End Work and does the same thing. If another technician is still working the ticket, only your part ends and the ticket stays open. If you are the last technician on it, stopping says the work is finished: you say where on site the vehicle is left, the ticket is completed and goes to the front office, and your next ticket moves up. Pickup and payment stay with the front office.',
    page: '/?tab=dayboard',
    gateType: 'soft',
    coachmarks: ['ticket-timer-controls'],
    icon: 'Square',
  },
  {
    id: 'tech-review-time',
    title: 'Reviewing your time',
    description:
      'After you stop, OptiBay records where your time belongs. When there is only one job, it all goes to that job automatically. When your time could belong to more than one job, OptiBay shows you Review Time: it proposes how your measured minutes divide, and you can adjust that before accepting it. Either way, recording time does not complete any job or close the ticket.',
    page: '/?tab=dayboard',
    gateType: 'soft',
    coachmarks: ['timer-review-dialog'],
    icon: 'ClipboardCheck',
  },
  {
    // Finish Work is a SEPARATE authority (`canCompleteTickets`, which is
    // deliberately not `canControlTimers`) for a ticket nobody is timing —
    // the last technician's Stop already completes a timed one. Deliberately
    // vague about how a job reaches "completed or skipped". See CORE-BL-GAP-013.
    id: 'tech-finish-work',
    title: 'Finishing the visit',
    description:
      'A ticket that nobody is timing is finished with its own deliberate action. When its work is done and its obligations are satisfied, a Finish Work button appears on the card. You are asked where the vehicle went — a parking spot or the general lot — and you have to say; silence is not taken as an answer. The server checks the ticket really is ready and can still refuse.',
    page: '/?tab=dayboard',
    gateType: 'soft',
    coachmarks: ['finish-work-button'],
    icon: 'CheckCircle',
  },
  {
    // The reference promise is route-gated. Telling somebody the guide is
    // permanently available when the router denies them /user-guide is a
    // promise they act on later, alone, at the moment they need it.
    id: 'tech-find-help',
    requiresRoute: '/user-guide',
    title: 'Finding this again',
    description:
      'The user guide is in the account menu and stays there. You can reopen this walkthrough any time — you are not expected to remember it all now.',
    page: '/',
    gateType: 'soft',
    coachmarks: ['account-menu-button'],
    icon: 'HelpCircle',
  },
];

// =============================================================================
// Detail Specialist — Core-100 R4, OD-14 (the fifth Core role)
// =============================================================================

/**
 * A detailer works the DETAIL jobs on a visit, with the same timer as a
 * technician, and finishes a visit only when it is detail work through and
 * through (OD-20: "any fully detail-only visit, even one another Detail
 * Specialist worked"). They are NOT taught the technician track: it teaches
 * mechanical job flow they may not take, and the server's assignment authority
 * (`technician_assignment_refusal`) refuses a detailer mechanical work.
 *
 * Every sentence below is checked against the landed authority:
 *   * timers: `canControlTimers` admits DETAIL_SPECIALIST (usePermissions);
 *   * completion: `complete_ticket_with_location` admits a Detail Specialist
 *     only for a fully detail-only visit (NOT_DETAIL_ONLY otherwise), and
 *     `__finish_work_facts` never lets a detailer's Stop finish a mixed visit
 *     (`completion_withheld`) — the front office is handed it instead.
 */
export const DETAIL_EXPERIENCE_STEPS: RoleAwareStep[] = [
  {
    id: 'detail-your-work',
    title: 'Your detail work for today',
    description:
      'The Dayboard shows the tickets with detail work assigned to you, in the order they are scheduled. Your day starts here.',
    page: '/?tab=dayboard',
    gateType: 'soft',
    coachmarks: ['dayboard-tab'],
    icon: 'Sparkles',
  },
  {
    id: 'detail-open-ticket',
    title: 'Your jobs on a visit',
    description:
      'Open a ticket to see the vehicle and its jobs. You work the detail jobs. If the same visit also has mechanical work, that work belongs to a technician — OptiBay will not assign it to you.',
    page: '/?tab=dayboard',
    gateType: 'soft',
    coachmarks: ['ticket-jobs-list'],
    icon: 'FileText',
  },
  {
    id: 'detail-start-work',
    title: 'Starting detail work',
    description:
      'Start your timer when you begin. If the ticket has more than one open job, you choose the job or jobs you are actually working on — nothing is selected for you, because the time you run is recorded against what you pick.',
    page: '/?tab=dayboard',
    gateType: 'soft',
    coachmarks: ['ticket-timer-controls'],
    icon: 'Timer',
  },
  {
    id: 'detail-pause',
    title: 'Stepping away — pause, then resume',
    description:
      'Pause when you break or wait on something. You give a short reason, paused time is not counted against the job, and you keep what you have already logged. Resume the same ticket when you come back. Do not use Stop for a break.',
    page: '/?tab=dayboard',
    gateType: 'soft',
    coachmarks: ['ticket-timer-controls'],
    icon: 'Pause',
  },
  {
    id: 'detail-stop-work',
    title: 'Ending your detail work',
    description:
      'Stop Work ends the work you have been measuring and sends that time to be reviewed. On a visit that is detail work only, if you are the last person working it, stopping finishes it: you say where the vehicle is left and it goes to the front office. On a visit that also has mechanical work, your Stop ends only your part — the visit is handed to the front office and is never finished by a detail Stop.',
    page: '/?tab=dayboard',
    gateType: 'soft',
    coachmarks: ['ticket-timer-controls'],
    icon: 'Square',
  },
  {
    id: 'detail-review-time',
    title: 'Reviewing your time',
    description:
      'When your time could belong to more than one job, OptiBay shows you Review Time: it proposes how your measured minutes divide, and you can adjust that before accepting it. Recording time does not complete any job or close the ticket.',
    page: '/?tab=dayboard',
    gateType: 'soft',
    coachmarks: ['timer-review-dialog'],
    icon: 'ClipboardCheck',
  },
  {
    id: 'detail-finish-work',
    title: 'Finishing a detail-only visit',
    description:
      'A visit that is detail work only and that nobody is timing is finished with Finish Work on the card. You are asked where the vehicle went, and you have to say. On a visit with any mechanical work the button is not yours to press — the front office finishes it — and the server refuses it if it is tried.',
    page: '/?tab=dayboard',
    gateType: 'soft',
    coachmarks: ['finish-work-button'],
    icon: 'CheckCircle',
  },
  {
    id: 'detail-find-help',
    requiresRoute: '/user-guide',
    title: 'Finding this again',
    description:
      'The user guide is in the account menu and stays there. You can reopen this walkthrough any time.',
    page: '/',
    gateType: 'soft',
    coachmarks: ['account-menu-button'],
    icon: 'HelpCircle',
  },
];

// =============================================================================
// Manager operations — running the day (Core-100 R4 W5, CORE-BL-GAP-011)
// =============================================================================

/**
 * The `manager` library teaches SETUP (shop profile, team, bays). Nothing taught a manager the DAY: reading the
 * board's load, what a technician's capacity line means, where the things that need a person are, how to use
 * capacity that opened up, and what the calendar's past and future days are. This is that track, offered to an
 * owner or manager next to `manager` (never replacing it, so existing completed `manager` rows keep their meaning),
 * under its own durable key `manager_ops_v1`.
 *
 * Every sentence was checked against the landed code: the OBLI load (`DayboardLoadIndicator`, capacity unknown is
 * said, not counted as zero), the technician capacity line and its efficiency basis (`tech-capacity-line`,
 * `tech-capacity-basis`), Needs attention and Open capacity (`canAssignTickets`; both suggest, neither moves
 * anything), and the calendar's projected / recomputed days (`calendarDayStatus`).
 */
export const MANAGER_OPS_EXPERIENCE_STEPS: RoleAwareStep[] = [
  {
    id: 'mops-the-day',
    title: 'The day at a glance',
    description:
      "The Dayboard is the shop's day: each technician's lane, in the order the work is scheduled. Running the day starts here, not in settings.",
    page: '/?tab=dayboard',
    gateType: 'soft',
    coachmarks: ['dayboard-tab'],
    icon: 'LayoutDashboard',
  },
  {
    id: 'mops-load',
    title: 'How loaded the day is',
    description:
      "The load index compares the work booked for the day with the time your technicians have. When someone's working hours are not set, their capacity is unknown and the board says so instead of counting it as zero.",
    page: '/?tab=dayboard',
    gateType: 'soft',
    coachmarks: ['obli-score'],
    icon: 'Gauge',
  },
  {
    id: 'mops-capacity-line',
    title: "A technician's capacity",
    description:
      'Each technician card shows booked minutes against the minutes they have today — after breaks, time away and their efficiency. The line under it says what that efficiency rests on; when it is not established, the capacity is an estimate.',
    page: '/?tab=dayboard',
    gateType: 'soft',
    coachmarks: ['tech-capacity-line'],
    icon: 'Users',
  },
  {
    id: 'mops-needs-attention',
    requiredCapability: 'canAssignTickets',
    title: 'What needs a person',
    description:
      "Needs attention collects what the shop's records say someone should look at: unexpected arrivals, timers left running, work running long and promises in trouble — missed and at risk said apart. Nothing there acts on its own.",
    page: '/?tab=dayboard',
    gateType: 'soft',
    coachmarks: ['needs-attention-button'],
    icon: 'AlertTriangle',
  },
  {
    id: 'mops-open-capacity',
    requiredCapability: 'canAssignTickets',
    title: 'Using time that opened up',
    description:
      'When work finishes early or a vehicle is ready to come in, Open capacity lists what could safely move into that time and why the rest cannot. You decide; leaving it unfilled is always allowed.',
    page: '/?tab=dayboard',
    gateType: 'soft',
    coachmarks: ['open-capacity-button'],
    icon: 'CalendarPlus',
  },
  {
    id: 'mops-calendar',
    title: 'Other days',
    description:
      "The calendar shows each day's load the same way the board does. A future day is a projection from what is booked now; a past day is recomputed from today's roster, not a record of that day.",
    page: '/?tab=dayboard',
    gateType: 'soft',
    coachmarks: ['calendar-date-button'],
    icon: 'Calendar',
  },
  {
    id: 'mops-find-help',
    requiresRoute: '/user-guide',
    title: 'Finding this again',
    description:
      'The user guide is in the account menu and stays there. You can reopen this walkthrough, or the setup one, any time from the same menu.',
    page: '/',
    gateType: 'soft',
    coachmarks: ['account-menu-button'],
    icon: 'HelpCircle',
  },
];

// =============================================================================
// Front of house — advisor and dispatcher
// =============================================================================

/**
 * Intake, scheduling, custody and the customer.
 *
 * An advisor is not a small manager: they do not configure the shop, edit
 * bays or manage technicians, and nothing here teaches those. A dispatcher
 * shares the schedule but not intake, which is handled by applicability
 * rather than by a second library.
 */
export const FOH_EXPERIENCE_STEPS: RoleAwareStep[] = [
  {
    id: 'foh-board',
    title: 'The board is the shop',
    description:
      'The Dayboard shows every ticket for the day, who has it and where it is. This is the shared picture — when it is accurate, nobody needs to ask.',
    page: '/?tab=dayboard',
    gateType: 'soft',
    coachmarks: ['dayboard-tab'],
    icon: 'LayoutDashboard',
  },
  {
    id: 'foh-create-ticket',
    title: 'Taking work in',
    description:
      'Create a ticket when a vehicle arrives. The customer and vehicle you attach here are what everything downstream refers to, so it is worth getting right at the door.',
    page: '/',
    gateType: 'soft',
    coachmarks: ['create-ticket-btn'],
    icon: 'PlusCircle',
    requiredCapability: 'canCreateTickets',
  },
  {
    id: 'foh-assign',
    title: 'Getting work to a technician',
    description:
      'Assign a ticket to a technician and a bay. OptiBay will suggest a placement; the choice stays yours.',
    page: '/',
    gateType: 'soft',
    coachmarks: ['create-ticket-btn'],
    icon: 'Users',
    requiredCapability: 'canAssignTickets',
  },
  {
    id: 'foh-custody',
    title: 'Handing work back and forth',
    description:
      'Work moves between the front of house and the bay. Handing a ticket over is an explicit action so the board always shows who actually holds it — which is what stops two people assuming the other has it.',
    page: '/?tab=dayboard',
    gateType: 'soft',
    coachmarks: ['ticket-custody-badge'],
    icon: 'ArrowLeftRight',
    requiredCapability: 'canReassignAdvisor',
  },
  {
    id: 'foh-find-tickets',
    title: 'Finding a ticket again',
    description:
      'Search and the date picker will find any ticket — including days that are not today. You do not need to keep a separate list.',
    page: '/',
    gateType: 'soft',
    coachmarks: ['inline-calendar'],
    icon: 'Search',
  },
  {
    id: 'foh-customer',
    title: 'Customer and vehicle records',
    description:
      'Customers and their vehicles persist between visits, so a returning car brings its history with it.',
    page: '/customers',
    gateType: 'soft',
    coachmarks: ['nav-customers-button'],
    icon: 'Car',
    requiredCapability: 'canViewCustomerInvoices',
  },
  {
    id: 'foh-find-help',
    requiresRoute: '/user-guide',
    title: 'Finding this again',
    description:
      'The user guide lives in the account menu and stays available. You can replay this walkthrough whenever you want it.',
    page: '/',
    gateType: 'soft',
    coachmarks: ['account-menu-button'],
    icon: 'HelpCircle',
  },
];

// =============================================================================
// Parts
// =============================================================================

/** A narrow surface, taught narrowly. */
export const PARTS_EXPERIENCE_STEPS: RoleAwareStep[] = [
  {
    id: 'parts-tickets',
    title: 'Seeing what work is in',
    description:
      'The board shows the tickets in the shop, so you can see what is being worked on and what is waiting.',
    page: '/?tab=dayboard',
    gateType: 'soft',
    coachmarks: ['dayboard-tab'],
    icon: 'LayoutDashboard',
  },
  {
    id: 'parts-parts',
    title: 'Parts on a ticket',
    description:
      'Parts attach to the ticket that needs them, so what was ordered stays connected to the job it was ordered for.',
    page: '/',
    gateType: 'soft',
    coachmarks: ['ticket-jobs-list'],
    icon: 'Package',
    requiredCapability: 'canViewParts',
  },
  {
    id: 'parts-ordering',
    title: 'Ordering',
    description:
      'Record an order against the ticket so the front of house can see what the job is waiting on without having to ask.',
    page: '/',
    gateType: 'soft',
    coachmarks: ['ticket-jobs-list'],
    icon: 'ShoppingCart',
    requiredCapability: 'canOrderParts',
  },
  {
    id: 'parts-find-help',
    requiresRoute: '/user-guide',
    title: 'Finding this again',
    description:
      'The user guide is in the account menu and stays there permanently.',
    page: '/',
    gateType: 'soft',
    coachmarks: ['account-menu-button'],
    icon: 'HelpCircle',
  },
];

// =============================================================================
// Read-only
// =============================================================================

/**
 * Viewing and reference only.
 *
 * An auditor can see the shop and cannot change it. Every step here is about
 * reading something; none teaches an action, because an action taught to
 * somebody whose role will refuse it is a lesson in frustration.
 */
export const READONLY_EXPERIENCE_STEPS: RoleAwareStep[] = [
  {
    id: 'readonly-scope',
    title: 'What you can see',
    description:
      'Your role gives you visibility across the shop without the ability to change it. Everything below is about finding and reading — nothing here will ask you to alter a record.',
    page: '/',
    gateType: 'soft',
    coachmarks: ['top-bar'],
    icon: 'Eye',
  },
  {
    id: 'readonly-board',
    title: 'The board',
    description:
      'The Dayboard shows the day as it is running: which tickets exist, who holds them and where they are.',
    page: '/?tab=dayboard',
    gateType: 'soft',
    coachmarks: ['dayboard-tab'],
    icon: 'LayoutDashboard',
  },
  {
    id: 'readonly-history',
    title: 'Looking at other days',
    description:
      'The date picker moves the board to any day, so a past day can be reviewed exactly as it ran.',
    page: '/',
    gateType: 'soft',
    coachmarks: ['inline-calendar'],
    icon: 'Calendar',
  },
  {
    id: 'readonly-find-help',
    requiresRoute: '/user-guide',
    title: 'Reference material',
    description:
      'The user guide is in the account menu and remains available permanently.',
    page: '/',
    gateType: 'soft',
    coachmarks: ['account-menu-button'],
    icon: 'HelpCircle',
  },
];

// =============================================================================
// Organisation-level roles with minimal Core surface
// =============================================================================

/**
 * Short and honest — and delivered somewhere these roles can actually stand.
 *
 * ORG_ADMIN, REGION_MANAGER and DISTRICT_MANAGER operate above an individual
 * shop, so inventing Core content for them would be teaching buttons they do
 * not have. This track orients them instead.
 *
 * WHY EVERY PAGE IS `/user-guide` AND NOT `/`
 *
 * It used to be `/`. `App.tsx` guards `/` to OWNER, MANAGER, SERVICE_WRITER
 * and TECHNICIAN, so every one of these lessons opened a 403 for every role on
 * this track. The content was written, the routing was correct, and the
 * destination refused all three of them.
 *
 * `/user-guide` admits ORG_ADMIN, and it is the honest home for a track whose
 * whole substance is "here is what this product is, here is what your access
 * covers, here is the reference".
 *
 * REGION_MANAGER and DISTRICT_MANAGER are not admitted to `/user-guide`
 * either — the only routes they hold are `/org/regions/:regionId` and
 * `/org/districts/:districtId`, which need a scope id this capability has no
 * way to supply. Their sequence therefore filters to empty and they are told
 * so plainly (`route_blocked`) rather than being walked into a 403. That is a
 * recorded integration gap, not a content gap — see CORE-BL-GAP-010.
 */
export const ORG_OVERVIEW_EXPERIENCE_STEPS: RoleAwareStep[] = [
  {
    id: 'org-what-core-is',
    title: 'What OptiBay Core does',
    description:
      'Core is the day-to-day operational record of a single shop: the tickets, who is working them, and how long the work actually took.',
    page: '/user-guide',
    gateType: 'soft',
    coachmarks: ['user-guide-page'],
    icon: 'Building2',
  },
  {
    id: 'org-your-core-access',
    title: 'Your access here',
    description:
      'Your role operates above an individual shop, so most day-to-day Core actions belong to the shop roles rather than to you. Rather than walk you through controls you will not see, this guide keeps to what applies.',
    page: '/user-guide',
    gateType: 'soft',
    coachmarks: ['user-guide-page'],
    icon: 'Info',
  },
  {
    id: 'org-find-help',
    requiresRoute: '/user-guide',
    title: 'Reference material',
    description:
      'The user guide covers the whole product, stays available, and is where this walkthrough has been taking place.',
    page: '/user-guide',
    gateType: 'soft',
    coachmarks: ['user-guide-page'],
    icon: 'HelpCircle',
  },
];

// =============================================================================
// Applicability
// =============================================================================

/**
 * Filter a track to what this person can actually do.
 *
 * A step with no audience metadata applies to everyone on the track. A step
 * naming a capability the viewer lacks is dropped — quietly, because its
 * absence is the correct outcome rather than something to explain.
 */
export function applicableSteps(
  steps: ReadonlyArray<RoleAwareStep>,
  role: KnownAppRole | null,
  capabilities: CapabilitySnapshot,
  /** Core-100 R4: the person's work roles, for route reachability (union, as ProtectedRoute). */
  workRoles?: ReadonlyArray<string> | null,
): OnboardingStep[] {
  return (steps ?? [])
    .filter((step) => {
      // A role gate narrows an audience. With the role unknown we cannot
      // establish membership of that audience, so the step is withheld —
      // teaching somebody a lesson written for a role they may not hold is
      // the failure this capability exists to stop.
      if (step.roles && (!role || !(step.roles as ReadonlyArray<string>).includes(role))) return false;
      if (step.requiredCapability) {
        if (capabilities?.[step.requiredCapability] !== true) return false;
      }
      // A step whose destination the role cannot open is not instruction.
      if (!canRoleOpen(step.page, role, workRoles)) return false;
      // …and neither is a promise about a page they cannot reach.
      if (step.requiresRoute && !canRoleOpen(step.requiresRoute, role, workRoles)) {
        return false;
      }
      return true;
    })
    .map(
      ({
        roles: _roles,
        requiredCapability: _cap,
        requiresRoute: _route,
        ...step
      }) => step,
    );
}
